import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { OrganizationProvider } from './hooks/useOrganization';
import Header from './components/Header';
import CreativesStudio from './pages/CreativesStudio';
import Campaigns from './pages/Campaigns';
import Assets from './pages/Assets';
// Guided selling deal flow
import {
  WorkQueue, BusinessDiscovery, BusinessWorkspace,
  PitchKit, PlanPicker,
  OfferBuilder, TargetingConfirmation, PricingScreen,
  ProposalBuilder, SendProposal, AcceptClose, LaunchChecklist,
  CampaignStatus as DealCampaignStatus, DealReport,
} from './pages/deal';
import { DealFlowProvider } from './hooks/useDealFlow';
import Debug from './pages/Debug';
import DirectorStudio from './pages/DirectorStudio';
import PublicProposal from './pages/PublicProposal';

// Self-serve media plan flow
import MediaPlanSearch from './pages/mediaplan/MediaPlanSearch';
import MediaPlan from './pages/mediaplan/MediaPlan';
import GenerateProgress from './pages/mediaplan/GenerateProgress';
import CampaignReview from './pages/mediaplan/CampaignReview';
import CampaignLive from './pages/mediaplan/CampaignLive';
import CampaignReport from './pages/mediaplan/CampaignReport';
import SelfServeDashboard from './pages/mediaplan/SelfServeDashboard';
import { CampaignFlowProvider } from './hooks/useMediaPlanFlow';

// Routing components
import RootRedirect from './components/routing/RootRedirect';
import OrgLayout from './components/routing/OrgLayout';
import ProtectedRoute, { OwnerRoute } from './components/routing/ProtectedRoute';

// Organization components
import OrgPicker from './components/organization/OrgPicker';
import NoOrgState from './components/organization/NoOrgState';

// Public-MVP feature flag
import { isPublicMvp, orgHomePath } from './lib/featureFlags';

// Analytics
import RouteAnalytics from './components/analytics/RouteAnalytics';

// Feedback widget
import FeedbackWidget from './components/feedback/FeedbackWidget';

// Helper component for org-scoped redirects to the org's home.
// In MVP mode this is Creative Studio; otherwise the seller work queue.
const OrgRedirectToHome = () => {
  const { orgSlug } = useParams();
  return <Navigate to={orgHomePath(orgSlug)} replace />;
};

/**
 * Hides a non-MVP route when the public-MVP flag is on, redirecting to the
 * Creative Studio instead. Preserves the route + component (no deletion) so
 * unsetting VITE_PUBLIC_MVP restores full access.
 */
const MvpHidden = ({ children }) => {
  const { orgSlug } = useParams();
  if (isPublicMvp()) {
    return <Navigate to={`/org/${orgSlug}/creatives`} replace />;
  }
  return children;
};

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isDirectorMode = location.pathname.includes('/director');
  const isCreativesMode = location.pathname.includes('/creatives');
  const isDealMode = location.pathname.includes('/deal');
  const isLeadsMode = location.pathname.includes('/leads') || isDealMode;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!loading && !user) {
    const loginUrl = import.meta.env.VITE_LOGIN_URL ||
      (window.location.hostname === 'localhost' ? 'http://localhost:8000/login.dev.html' : 'https://cornercast-336b3.web.app/login.html');
    window.location.href = loginUrl;
    return null;
  }

  return (
    <div className={`min-h-screen flex flex-col overflow-x-clip bg-background ${isDirectorMode ? 'h-screen overflow-hidden' : ''}`}>
      <Routes>
        {/* Root redirect based on user state */}
        <Route path="/" element={<RootRedirect />} />

        {/* Organization picker for multi-org users */}
        <Route path="/organizations" element={
          <>
            <Header />
            <main className="flex-1 py-8">
              <OrgPicker />
            </main>
          </>
        } />

        {/* No organization state */}
        <Route path="/waiting" element={<NoOrgState />} />

        {/* Org-scoped routes */}
        <Route path="/org/:orgSlug/*" element={
          <>
            <Header />
            <main className={`flex-1 overflow-x-clip ${isDirectorMode ? 'min-h-0 overflow-hidden !p-0 !max-w-full' : isCreativesMode || isLeadsMode ? '' : 'py-8'}`}>
              <Routes>
                <Route element={<OrgLayout />}>
                  {/* MVP-visible: the paid Creative Studio flow + asset library */}
                  <Route path="creatives" element={<CreativesStudio />} />
                  <Route path="creatives/job/:jobId" element={<CreativesStudio />} />
                  <Route path="assets" element={<Assets />} />
                  {/* Non-MVP areas: preserved, but hidden behind the public-MVP
                      flag (MvpHidden redirects to Creative Studio when on). */}
                  <Route path="leads" element={<MvpHidden><WorkQueue /></MvpHidden>} />
                  {/* Guided selling deal flow */}
                  <Route path="deal/new" element={<MvpHidden><BusinessDiscovery /></MvpHidden>} />
                  {/* ConfirmScope removed — scope now asked inline in OfferBuilder */}
                  <Route path="deal/business/:businessId" element={<MvpHidden><BusinessWorkspace /></MvpHidden>} />
                  <Route path="deal/:dealId/pitch" element={<MvpHidden><PitchKit /></MvpHidden>} />
                  <Route path="deal/:dealId/plan" element={<MvpHidden><PlanPicker /></MvpHidden>} />
                  <Route path="deal/:dealId/offer" element={<MvpHidden><OfferBuilder /></MvpHidden>} />
                  <Route path="deal/:dealId/targeting" element={<MvpHidden><TargetingConfirmation /></MvpHidden>} />
                  <Route path="deal/:dealId/pricing" element={<MvpHidden><PricingScreen /></MvpHidden>} />
                  <Route path="deal/:dealId/proposal" element={<MvpHidden><ProposalBuilder /></MvpHidden>} />
                  <Route path="deal/:dealId/send" element={<MvpHidden><SendProposal /></MvpHidden>} />
                  <Route path="deal/:dealId/close" element={<MvpHidden><AcceptClose /></MvpHidden>} />
                  <Route path="deal/:dealId/launch" element={<MvpHidden><LaunchChecklist /></MvpHidden>} />
                  <Route path="deal/:dealId/live" element={<MvpHidden><DealCampaignStatus /></MvpHidden>} />
                  <Route path="deal/:dealId/report" element={<MvpHidden><DealReport /></MvpHidden>} />
                  <Route path="campaigns" element={<MvpHidden><Campaigns /></MvpHidden>} />
                  <Route path="director" element={<OwnerRoute><DirectorStudio /></OwnerRoute>} />
                  <Route path="director/:jobId" element={<OwnerRoute><DirectorStudio /></OwnerRoute>} />
                  <Route path="debug/:jobId" element={<MvpHidden><Debug /></MvpHidden>} />
                  <Route path="" element={<OrgRedirectToHome />} />
                  <Route path="*" element={<OrgRedirectToHome />} />
                </Route>
              </Routes>
            </main>
          </>
        } />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FeedbackWidget />
      <Toaster position="top-right" richColors />
    </div>
  );
}

/**
 * Self-serve media plan flow — no auth required until payment.
 * These routes live outside the AuthProvider/OrgProvider gates.
 */
function SelfServeRoutes() {
  return (
    <Routes>
      <Route path="/campaign/new" element={<MediaPlanSearch />} />
      <Route path="/campaign/:planId/plan" element={<MediaPlan />} />
      <Route path="/campaign/:planId/generate" element={<GenerateProgress />} />
      <Route path="/campaign/:planId/review" element={<CampaignReview />} />
      <Route path="/campaign/:planId/live" element={<CampaignLive />} />
      <Route path="/campaign/:planId/report" element={<CampaignReport />} />
      <Route path="/dashboard" element={<SelfServeDashboard />} />
    </Routes>
  );
}

function PublicProposalRoutes() {
  return (
    <Routes>
      <Route path="/public/proposals/:token" element={<PublicProposal />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRouter() {
  const location = useLocation();
  const isSelfServe = location.pathname.startsWith('/campaign/') || location.pathname === '/dashboard';
  const isPublicProposal = location.pathname.startsWith('/public/proposals/');

  // In public-MVP mode the self-serve campaign flow is hidden; send users into
  // the main app (which routes to the paid Creative Studio).
  if (isSelfServe && isPublicMvp()) {
    return <Navigate to="/" replace />;
  }

  if (isSelfServe) {
    return (
      <div className="min-h-screen flex flex-col overflow-x-clip bg-background">
        <SelfServeRoutes />
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  if (isPublicProposal) {
    return (
      <div className="min-h-screen flex flex-col overflow-x-clip bg-background">
        <PublicProposalRoutes />
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <AuthProvider>
      <OrganizationProvider>
        <AppContent />
      </OrganizationProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router>
      <RouteAnalytics />
      <CampaignFlowProvider>
        <DealFlowProvider>
          <AppRouter />
        </DealFlowProvider>
      </CampaignFlowProvider>
    </Router>
  );
}

export default App;
